import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Icon } from '@/components/Icon';
import { LoadingRegion, Skeleton } from '@/components/Skeleton';
import { cn } from '@/lib/cn';
import { formatParty, isLocationType } from '@/venue/venue';
import type { AssistanceRequest } from './api';
import { EdgeBar, Notice } from './parts';
import { useAssistance, useResolveAssistance, useStaffLive } from './queries';
import { assistanceLabel, describeStaffError, locationText, waitedText } from './wording';
import { PageHeader } from '@/components/PageHeader';
import { cardClasses } from '@/components/Card';

/** Redraws "5 min ago" once a minute. The time a request was made comes from the server. */
function useNow() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  return now;
}

/** One open request: what was asked, the note, how long ago, and its own Resolve. */
function RequestLine({ request, now }: { request: AssistanceRequest; now: number }) {
  const resolve = useResolveAssistance();
  const where = locationText(request.location_type, request.location_label);
  return (
    <li className="flex flex-col gap-2 py-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Badge tone="attention">{assistanceLabel(request.type)}</Badge>
          <span className="text-sm text-muted">{waitedText(request.created_at, now)}</span>
        </div>
        <Button
          variant="primary"
          loading={resolve.isPending}
          aria-label={`Resolve ${assistanceLabel(request.type)} at ${where}`}
          onClick={() => resolve.mutate(request.request_id)}
        >
          Resolve
        </Button>
      </div>
      {request.note && (
        <p className="rounded-control bg-attention-soft px-3 py-1.5 text-sm break-words text-attention-ink">
          &ldquo;{request.note}&rdquo;
        </p>
      )}
      {resolve.error && (
        <Notice tone="error">{describeStaffError(resolve.error, 'resolve')}</Notice>
      )}
    </li>
  );
}

/** The open requests of one session, one row each, oldest first. The session page shows these alone. */
export function RequestLines({
  requests,
  now,
  className,
}: {
  requests: AssistanceRequest[];
  now: number;
  className?: string;
}) {
  return (
    <ul className={cn('flex flex-col divide-y divide-line', className)}>
      {requests.map((request) => (
        <RequestLine key={request.request_id} request={request} now={now} />
      ))}
    </ul>
  );
}

/**
 * One table or seat that asked for help: an amber bar on the left edge, the address once, then one row per
 * open request with its own Resolve, and one way to the session.
 */
function RequestCard({ requests, now }: { requests: AssistanceRequest[]; now: number }) {
  const first = requests[0]!;
  const where = locationText(first.location_type, first.location_label);
  const party = isLocationType(first.location_type)
    ? formatParty(first.location_type, first.party_display_name)
    : first.party_display_name;
  return (
    <li
      aria-label={where}
      className={cn(
        cardClasses({ padding: 'none' }),
        'relative flex flex-col gap-1 overflow-hidden py-3 pr-3 pl-4',
      )}
    >
      <EdgeBar tone="attention" />
      <div className="min-w-0">
        <p className="text-base font-bold break-words text-ink">{where}</p>
        <p className="text-sm break-words text-muted">
          {party}
          {first.zone_name ? ` · ${first.zone_name}` : ''}
        </p>
      </div>
      <RequestLines requests={requests} now={now} />
      <div className="flex items-center border-t border-line pt-1">
        <Link
          to={`/staff/sessions/${first.session_id}`}
          className="inline-flex min-h-11 items-center gap-1 rounded-control px-2 text-sm font-semibold text-primary no-underline hover:bg-primary-soft"
        >
          View session
          <Icon name="chevronRight" size={16} />
        </Link>
      </div>
    </li>
  );
}

/** Requests grouped by session, each group in the order of its oldest request. `list` is oldest first. */
function groupBySession(list: AssistanceRequest[]): AssistanceRequest[][] {
  const groups = new Map<string, AssistanceRequest[]>();
  for (const request of list) {
    groups.set(request.session_id, [...(groups.get(request.session_id) ?? []), request]);
  }
  return [...groups.values()];
}

export function RequestsPage() {
  useStaffLive();
  const requests = useAssistance();
  const now = useNow();
  // Oldest first: the customer who has waited longest is at the top.
  const list = [...(requests.data ?? [])].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );
  return (
    <section aria-labelledby="requests-title" className="flex flex-col gap-4">
      <PageHeader
        id="requests-title"
        title="Requests"
        subtitle={
          requests.data
            ? list.length === 0
              ? undefined
              : list.length === 1
                ? '1 open request, longest wait first'
                : `${list.length} open requests, longest wait first`
            : undefined
        }
      />
      {requests.isPending && (
        <LoadingRegion slowHint label="Loading requests">
          <div className="grid gap-2 md:grid-cols-2">
            <Skeleton className="h-28" />
            <Skeleton className="h-28" />
          </div>
        </LoadingRegion>
      )}
      {requests.isError && !requests.data && (
        <ErrorState error={requests.error} onRetry={() => void requests.refetch()} />
      )}
      {requests.isError && requests.data && (
        <Notice
          tone="warn"
          action={
            <Button variant="secondary" onClick={() => void requests.refetch()}>
              Try again
            </Button>
          }
        >
          Couldn&apos;t refresh the requests. Showing the last list received.
        </Notice>
      )}
      {requests.data && list.length === 0 && (
        <EmptyState
          icon="bell"
          title="No open requests"
          message="Requests appear here when a customer asks for help."
        />
      )}
      {list.length > 0 && (
        <ul aria-label="Open requests" className="grid items-start gap-2 md:grid-cols-2">
          {groupBySession(list).map((group) => (
            <RequestCard key={group[0]!.session_id} requests={group} now={now} />
          ))}
        </ul>
      )}
    </section>
  );
}
